import { ReactNode, useMemo } from 'react';
import { Box, Stack, ThemeProvider, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { CheckCircleFilled, SaveOutlined, ThunderboltOutlined, WalletOutlined } from '@ant-design/icons';
import { DISPLAY_FONT } from 'components/ui/tokens';
import { FreezeEyebrow, FreezeFloatingFlake } from 'features/subscription-freeze/components/FreezeHeroParts';
import FreezeSnowfall from 'features/subscription-freeze/components/FreezeSnowfall';
import { freezeFrostSx, freezeGlintSx, freezeSheenSx } from 'features/subscription-freeze/components/freezeMotion';
import {
  FREEZE_LAYOUT,
  FreezeMonoLabel,
  PRESERVATION_INK,
  preservationHeroSx,
  SnowflakeMark,
  TEMPERATURE
} from 'features/subscription-freeze/components/freezeUi';
import { glacialTheme } from 'features/subscription-freeze/components/glacialTheme';
import { FREEZE_NAME } from 'features/subscription-freeze/content';
import { PAUSE_STEP } from '../content';

const ICE = TEMPERATURE.ice;

const PERK_ICONS: Record<(typeof PAUSE_STEP.perks)[number]['key'], ReactNode> = {
  credit: <WalletOutlined />,
  setup: <SaveOutlined />,
  early: <ThunderboltOutlined />
};

// A dot at each end of the pause: lit where it starts, hollow where billing comes back.
function TimelineDot({ lit }: { lit?: boolean }) {
  return (
    <Box
      aria-hidden
      sx={{
        width: 12,
        height: 12,
        flexShrink: 0,
        borderRadius: '50%',
        border: `2px solid ${ICE.accent}`,
        bgcolor: lit ? ICE.accent : PRESERVATION_INK.surface,
        boxShadow: `0 0 0 4px ${ICE.soft}`
      }}
    />
  );
}

function TimelineEnd({ label, value, align }: { label: string; value: string; align: 'left' | 'right' }) {
  return (
    <Box sx={{ textAlign: align, minWidth: 0 }}>
      <Box component="dt">
        <FreezeMonoLabel>{label}</FreezeMonoLabel>
      </Box>
      <Typography component="dd" sx={{ m: 0, mt: 0.75, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
    </Box>
  );
}

/** The pause offer in the freeze page's own look: midnight, falling ice, its length, and the road to the restart. */
export default function PauseOfferHero({ days, restartsOn }: { days: number; restartsOn: string }) {
  const base = useTheme();
  // Always midnight, whichever theme the dialog is in, as the freeze page is.
  const theme = useMemo(() => glacialTheme(base, 'ice'), [base]);

  return (
    <Stack spacing={1.5}>
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            ...preservationHeroSx('ice'),
            ...freezeFrostSx(),
            p: { xs: 2.25, sm: 3 },
            textAlign: 'left',
            borderRadius: FREEZE_LAYOUT.panelRadius,
            border: `1px solid ${PRESERVATION_INK.line}`
          }}
        >
          <FreezeSnowfall density={18} />
          <FreezeFloatingFlake sx={{ top: -56, right: -56, width: 220, height: 220 }} />

          <Stack spacing={3} sx={{ position: 'relative', zIndex: 1 }}>
            <Box>
              <FreezeEyebrow label={FREEZE_NAME} />
              {/* The length and what it costs read as one line: the badge wraps under it on a phone. */}
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 1.5 }}>
                <Stack direction="row" alignItems="baseline" spacing={1}>
                  <Typography
                    sx={{
                      fontFamily: DISPLAY_FONT,
                      fontSize: { xs: 56, sm: 68 },
                      fontWeight: 700,
                      lineHeight: 0.9,
                      letterSpacing: '-0.05em',
                      fontVariantNumeric: 'tabular-nums'
                    }}
                  >
                    {days}
                  </Typography>
                  <Typography
                    sx={{ fontFamily: DISPLAY_FONT, fontStyle: 'italic', fontSize: { xs: 20, sm: 24 }, fontWeight: 500, color: ICE.accent }}
                  >
                    {PAUSE_STEP.unit(days)}
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="center"
                  sx={{
                    ...freezeGlintSx(),
                    px: 1.5,
                    py: 0.75,
                    borderRadius: FREEZE_LAYOUT.pill,
                    color: ICE.ink,
                    bgcolor: ICE.accent,
                    boxShadow: `0 0 0 4px ${ICE.soft}, 0 8px 24px -10px ${ICE.accent}`
                  }}
                >
                  <CheckCircleFilled aria-hidden style={{ fontSize: 15 }} />
                  <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.2 }}>{PAUSE_STEP.noCharge}</Typography>
                </Stack>
              </Box>
            </Box>

            <Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <TimelineDot lit />
                <Box
                  aria-hidden
                  sx={{
                    ...freezeSheenSx(),
                    flex: 1,
                    height: 4,
                    borderRadius: 2,
                    background: `linear-gradient(90deg, ${ICE.accent}, ${alpha(ICE.accent, 0.2)})`
                  }}
                />
                <Box aria-hidden sx={{ color: ICE.accent, display: 'flex' }}>
                  <SnowflakeMark size={18} />
                </Box>
                <Box aria-hidden sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: alpha(ICE.accent, 0.2) }} />
                <TimelineDot />
              </Stack>
              <Stack component="dl" direction="row" justifyContent="space-between" spacing={2} sx={{ m: 0, mt: 1.5 }}>
                <TimelineEnd label={PAUSE_STEP.starts} value={PAUSE_STEP.today} align="left" />
                <TimelineEnd label={PAUSE_STEP.restarts} value={restartsOn} align="right" />
              </Stack>
            </Box>

            <Box
              component="ul"
              sx={{
                m: 0,
                p: 0,
                listStyle: 'none',
                display: 'grid',
                gap: 1,
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }
              }}
            >
              {PAUSE_STEP.perks.map((perk) => (
                <Stack
                  key={perk.key}
                  component="li"
                  direction={{ xs: 'row', sm: 'column' }}
                  spacing={{ xs: 1.5, sm: 1 }}
                  alignItems={{ xs: 'center', sm: 'flex-start' }}
                  sx={{
                    p: 1.5,
                    borderRadius: FREEZE_LAYOUT.controlRadius,
                    border: `1px solid ${PRESERVATION_INK.hairline}`,
                    bgcolor: PRESERVATION_INK.glass,
                    backdropFilter: 'blur(4px)'
                  }}
                >
                  <Box
                    aria-hidden
                    sx={{
                      width: 30,
                      height: 30,
                      flexShrink: 0,
                      borderRadius: '50%',
                      display: 'grid',
                      placeItems: 'center',
                      fontSize: 15,
                      color: ICE.accent,
                      bgcolor: ICE.soft
                    }}
                  >
                    {PERK_ICONS[perk.key]}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {perk.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
                      {perk.body}
                    </Typography>
                  </Box>
                </Stack>
              ))}
            </Box>
          </Stack>
        </Box>
      </ThemeProvider>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
        {PAUSE_STEP.footnote}
      </Typography>
    </Stack>
  );
}
